/**
 * AuthContext — estado de sesión del Admin Web.
 *
 * - El `accessToken` vive solo en memoria (ref), nunca en `localStorage`.
 * - Al montar, intenta `POST /api/v1/admin/auth/refresh` (cookie httpOnly) para restaurar la sesión.
 * - Expone el login (primer factor), el 2FA, el enrolamiento y el logout.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { configureApiClient } from '@/lib/http/apiClient';
import { authApi } from '../api/authApi';
import {
  clearLoginEmail,
  clearPendingUserId,
  saveLoginEmail,
  savePendingUserId,
} from '../storage';
import {
  isAuthTokensResponse,
  type AuthTokensResponse,
  type AuthUser,
  type EnrollCompleteInput,
  type LoginResponse,
} from '../types';

export type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Primer factor. Devuelve la respuesta para decidir el siguiente paso (2FA/enrolar). */
  login: (email: string, password: string) => Promise<LoginResponse>;
  /** Segundo factor. Abre sesión si el código es válido. */
  verifyTotp: (userId: string, code: string) => Promise<void>;
  /** Primer acceso: completa el enrolamiento y abre sesión. */
  completeEnrollment: (input: EnrollCompleteInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [user, setUser] = useState<AuthUser | null>(null);
  const tokenRef = useRef<string | null>(null);
  // Single-flight: el refresh rota la sesión en el backend; dos llamadas concurrentes
  // (p. ej. StrictMode) invalidarían el token. Se comparte la misma promesa.
  const refreshPromiseRef = useRef<Promise<AuthTokensResponse | null> | null>(null);

  const applySession = useCallback((tokens: AuthTokensResponse) => {
    tokenRef.current = tokens.accessToken;
    setUser(tokens.user);
    setStatus('authenticated');
  }, []);

  const clearSession = useCallback(() => {
    tokenRef.current = null;
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  /** Refresca la sesión (deduplicado) y aplica/limpia el estado según el resultado. */
  const restoreSession = useCallback(async (): Promise<AuthTokensResponse | null> => {
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = (async () => {
        try {
          const response = await authApi.refresh();
          return isAuthTokensResponse(response) ? response : null;
        } catch {
          return null;
        }
      })().finally(() => {
        refreshPromiseRef.current = null;
      });
    }
    const tokens = await refreshPromiseRef.current;
    if (tokens) {
      applySession(tokens);
    } else {
      clearSession();
    }
    return tokens;
  }, [applySession, clearSession]);

  // Restauración de sesión al cargar la app (cookie httpOnly de refresh).
  useEffect(() => {
    void restoreSession();
  }, [restoreSession]);

  // Conecta el cliente HTTP con el token en memoria y el refresh.
  useEffect(() => {
    configureApiClient({
      getAccessToken: () => tokenRef.current,
      refresh: async () => {
        const tokens = await restoreSession();
        return tokens ? tokens.accessToken : null;
      },
      onUnauthorized: clearSession,
    });
    return () => {
      configureApiClient({ getAccessToken: () => null, refresh: null, onUnauthorized: null });
    };
  }, [restoreSession, clearSession]);

  const login = useCallback(async (email: string, password: string): Promise<LoginResponse> => {
    const response = await authApi.login(email, password);
    saveLoginEmail(email);
    if (response.userId) {
      savePendingUserId(response.userId);
    }
    return response;
  }, []);

  const verifyTotp = useCallback(
    async (userId: string, code: string): Promise<void> => {
      const tokens = await authApi.verifyTotp(userId, code);
      clearPendingUserId();
      applySession(tokens);
    },
    [applySession],
  );

  const completeEnrollment = useCallback(
    async (input: EnrollCompleteInput): Promise<void> => {
      const tokens = await authApi.enrollComplete(input);
      clearPendingUserId();
      clearLoginEmail();
      applySession(tokens);
    },
    [applySession],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // La sesión ya puede estar inválida: el state local manda.
    } finally {
      clearPendingUserId();
      clearLoginEmail();
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, verifyTotp, completeEnrollment, logout }),
    [status, user, login, verifyTotp, completeEnrollment, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook de acceso a la sesión. Debe usarse dentro de `<AuthProvider>`. */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }
  return context;
}
